import { useState, useEffect, useCallback, useRef } from 'react';
import type { CustomerServiceStatus, ServiceStatusScope, CustomerServiceCreateRequest } from '@techgitter/contracts';
import type { CustomerServiceUpdateRequest } from '@techgitter/contracts';
import { getCustomerServices, createCustomerService, updateCustomerService } from '../services/customerServicesApi';

const CUSTOMER_SERVICES_UPDATED_EVENT = 'customer-services-updated';

interface CustomerServicesUpdatedDetail {
  dataverseCustomerId: string;
  sourceId?: string;
}

const customerServicesEventTarget = new EventTarget();

function emitCustomerServicesUpdated(detail: CustomerServicesUpdatedDetail): void {
  customerServicesEventTarget.dispatchEvent(
    new CustomEvent<CustomerServicesUpdatedDetail>(CUSTOMER_SERVICES_UPDATED_EVENT, { detail })
  );
}

interface UseCustomerServicesResult {
  services: CustomerServiceStatus[];
  isLoading: boolean;
  error: string | null;
  canEditServices: boolean;
  refresh: () => void;
  create: (payload: CustomerServiceCreateRequest) => Promise<void>;
  update: (customerServiceId: string, updates: CustomerServiceUpdateRequest) => Promise<void>;
}

export function useCustomerServices(
  dataverseCustomerId: string | null,
  scope: ServiceStatusScope = 'all'
): UseCustomerServicesResult {
  const hookInstanceIdRef = useRef(`customer-services-${Math.random().toString(36).slice(2)}`);
  const [services, setServices] = useState<CustomerServiceStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadServices = useCallback(async (signal?: AbortSignal) => {
    if (!dataverseCustomerId) {
      setServices([]);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getCustomerServices(dataverseCustomerId, scope, signal);
      if (signal?.aborted) return;
      setServices(result?.items || []);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to load services';
      setError(errorMessage);
      setServices([]);
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  }, [dataverseCustomerId, scope]);

  useEffect(() => {
    const controller = new AbortController();
    loadServices(controller.signal);
    return () => controller.abort();
  }, [loadServices]);

  useEffect(() => {
    if (!dataverseCustomerId) return;

    const handleServicesUpdated = (event: Event) => {
      const detail = (event as CustomEvent<CustomerServicesUpdatedDetail>).detail;
      if (!detail || detail.dataverseCustomerId !== dataverseCustomerId) return;
      if (detail.sourceId === hookInstanceIdRef.current) return;
      loadServices();
    };

    customerServicesEventTarget.addEventListener(CUSTOMER_SERVICES_UPDATED_EVENT, handleServicesUpdated);
    return () => {
      customerServicesEventTarget.removeEventListener(CUSTOMER_SERVICES_UPDATED_EVENT, handleServicesUpdated);
    };
  }, [dataverseCustomerId, loadServices]);

  const refresh = useCallback(() => {
    loadServices();
  }, [loadServices]);

  const create = useCallback(async (payload: CustomerServiceCreateRequest) => {
    if (!dataverseCustomerId) {
      throw new Error('No customer selected');
    }

    try {
      await createCustomerService(dataverseCustomerId, payload);
      await loadServices();
      emitCustomerServicesUpdated({
        dataverseCustomerId,
        sourceId: hookInstanceIdRef.current,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create service';
      setError(errorMessage);
      throw err;
    }
  }, [dataverseCustomerId, loadServices]);

  const update = useCallback(async (customerServiceId: string, updates: CustomerServiceUpdateRequest) => {
    if (!dataverseCustomerId) {
      throw new Error('No customer selected');
    }

    try {
      await updateCustomerService(dataverseCustomerId, customerServiceId, updates);
      await loadServices();
      emitCustomerServicesUpdated({
        dataverseCustomerId,
        sourceId: hookInstanceIdRef.current,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update service';
      setError(errorMessage);
      throw err;
    }
  }, [dataverseCustomerId, loadServices]);

  const canEditServices = Boolean(dataverseCustomerId);

  return {
    services,
    isLoading,
    error,
    canEditServices,
    refresh,
    create,
    update,
  };
}
